import { useState, useMemo } from 'react';
import { Address, AddressContent } from 'models/profile-settings.model';
import { flattenAddressContents } from 'utils/address.utils';
import { US_STATES } from 'common/constants';

export const useAddressForm = (
  initialMailingAddress: Address,
  initialResidentialAddress: Address,
) => {
  const contentsFromAddress = (
    address: Address,
  ): AddressContent['contents'] => [
    {
      labelId: 'STR_STREET_ADDRESS',
      value: address?.address1 || '',
      type: 'text',
    },
    {
      labelId: 'STR_APT_SUITE_UNIT_FLOOR',
      value: address?.address2 || '',
      type: 'text',
    },
    { labelId: 'STR_CITY', value: address?.city || '', type: 'text' },
    {
      labelId: 'STR_STATE',
      value: address?.state || '',
      type: 'select',
      options: US_STATES,
    },
    { labelId: 'STR_POSTAL_CODE', value: address?.postal || '', type: 'text' },
  ];

  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [hasMailingChanges, setHasMailingChanges] = useState(false);
  const [hasHomeChanges, setHasHomeChanges] = useState(false);

  // Use useMemo for addresses to prevent unnecessary re-computations
  const addresses = useMemo(
    (): AddressContent[] => [
      {
        type: 'mailing',
        title: 'STR_MAILING_ADDRESS',
        description: 'STR_MAILING_ADDRESS_DESC',
        contents: contentsFromAddress(initialMailingAddress),
      },
      {
        type: 'home',
        title: 'STR_RESIDENTIAL_ADDRESS',
        description: 'STR_RESIDENTIAL_ADDRESS_DESC',
        contents: contentsFromAddress(initialResidentialAddress),
      },
    ],
    [initialMailingAddress, initialResidentialAddress],
  );

  // Use useMemo for contents to prevent unnecessary re-computations
  const contents = useMemo(
    () => flattenAddressContents(addresses),
    [addresses],
  );

  const handleContent = (mailing: Address, home: Address) => {
    const newAddresses: AddressContent[] = [
      {
        type: 'mailing',
        title: 'STR_MAILING_ADDRESS',
        description: 'STR_MAILING_ADDRESS_DESC',
        contents: contentsFromAddress(mailing),
      },
      {
        type: 'home',
        title: 'STR_RESIDENTIAL_ADDRESS',
        description: 'STR_RESIDENTIAL_ADDRESS_DESC',
        contents: contentsFromAddress(home),
      },
    ];
    // No need to set state here - just return the new addresses
    return flattenAddressContents(newAddresses);
  };

  const transformAddress = (
    values: Record<string, string>,
    initialAddress: Address,
    type: string,
  ): Address => ({
    address1: values[`STR_STREET_ADDRESS_${type}`] || initialAddress.address1,
    address2:
      values[`STR_APT_SUITE_UNIT_FLOOR_${type}`] ||
      initialAddress.address2 ||
      '',
    city: values[`STR_CITY_${type}`] || initialAddress.city,
    state: values[`STR_STATE_${type}`] || initialAddress.state,
    postal: values[`STR_POSTAL_CODE_${type}`] || initialAddress.postal,
  });

  // Check if any address has changes (for overall state)
  const hasAnyAddressChanges = hasMailingChanges || hasHomeChanges;

  const canSave = useMemo(() => {
    if (!hasAnyAddressChanges) return true; // No changes = can save

    // Only validate the address that has changes
    const areAllAddressFieldsComplete = (addressType: 'mailing' | 'home') => {
      const requiredFields = [
        'STR_STREET_ADDRESS',
        'STR_CITY',
        'STR_STATE',
        'STR_POSTAL_CODE',
      ];

      return requiredFields.every((field) => {
        const fieldId = `${field}_${addressType}`;
        const editedValue = editValues[fieldId] || '';
        let currentValue = editedValue;

        // If no edited value, use the initial API value
        if (!editedValue) {
          if (addressType === 'mailing') {
            if (field.includes('STR_STREET_ADDRESS')) {
              currentValue = initialMailingAddress.address1 || '';
            } else if (field.includes('STR_APT_SUITE_UNIT_FLOOR')) {
              currentValue = initialMailingAddress.address2 || '';
            } else if (field.includes('STR_CITY')) {
              currentValue = initialMailingAddress.city || '';
            } else if (field.includes('STR_STATE')) {
              currentValue = initialMailingAddress.state || '';
            } else if (field.includes('STR_POSTAL_CODE')) {
              currentValue = initialMailingAddress.postal || '';
            }
          } else if (addressType === 'home') {
            if (field.includes('STR_STREET_ADDRESS')) {
              currentValue = initialResidentialAddress.address1 || '';
            } else if (field.includes('STR_APT_SUITE_UNIT_FLOOR')) {
              currentValue = initialResidentialAddress.address2 || '';
            } else if (field.includes('STR_CITY')) {
              currentValue = initialResidentialAddress.city || '';
            } else if (field.includes('STR_STATE')) {
              currentValue = initialResidentialAddress.state || '';
            } else if (field.includes('STR_POSTAL_CODE')) {
              currentValue = initialResidentialAddress.postal || '';
            }
          }
        }

        return currentValue.trim() !== '';
      });
    };

    // If only mailing has changes, only validate mailing
    if (hasMailingChanges && !hasHomeChanges) {
      return areAllAddressFieldsComplete('mailing');
    }

    // If only home has changes, only validate home
    if (hasHomeChanges && !hasMailingChanges) {
      return areAllAddressFieldsComplete('home');
    }

    // If both have changes, validate both
    if (hasMailingChanges && hasHomeChanges) {
      return (
        areAllAddressFieldsComplete('mailing') &&
        areAllAddressFieldsComplete('home')
      );
    }

    return true;
  }, [
    hasAnyAddressChanges,
    hasMailingChanges,
    hasHomeChanges,
    editValues,
    initialMailingAddress,
    initialResidentialAddress,
  ]);

  const getFieldError = (fieldId: string) => {
    // Determine which address type this field belongs to
    const isMailingField = fieldId.includes('_mailing');
    const isHomeField = fieldId.includes('_home');

    // Only show validation if the specific address type has changes
    if (isMailingField && !hasMailingChanges) return '';
    if (isHomeField && !hasHomeChanges) return '';

    // Get the current value (either edited or initial)
    const editedValue = editValues[fieldId] || '';
    let currentValue = editedValue;

    // If no edited value, use the initial API value
    if (!editedValue) {
      if (isMailingField) {
        if (fieldId.includes('STR_STREET_ADDRESS_mailing')) {
          currentValue = initialMailingAddress.address1 || '';
        } else if (fieldId.includes('STR_APT_SUITE_UNIT_FLOOR_mailing')) {
          currentValue = initialMailingAddress.address2 || '';
        } else if (fieldId.includes('STR_CITY_mailing')) {
          currentValue = initialMailingAddress.city || '';
        } else if (fieldId.includes('STR_STATE_mailing')) {
          currentValue = initialMailingAddress.state || '';
        } else if (fieldId.includes('STR_POSTAL_CODE_mailing')) {
          currentValue = initialMailingAddress.postal || '';
        }
      } else if (isHomeField) {
        if (fieldId.includes('STR_STREET_ADDRESS_home')) {
          currentValue = initialResidentialAddress.address1 || '';
        } else if (fieldId.includes('STR_APT_SUITE_UNIT_FLOOR_home')) {
          currentValue = initialResidentialAddress.address2 || '';
        } else if (fieldId.includes('STR_CITY_home')) {
          currentValue = initialResidentialAddress.city || '';
        } else if (fieldId.includes('STR_STATE_home')) {
          currentValue = initialResidentialAddress.state || '';
        } else if (fieldId.includes('STR_POSTAL_CODE_home')) {
          currentValue = initialResidentialAddress.postal || '';
        }
      }
    }

    // Only validate required fields (exclude apt/suite)
    const requiredFields = [
      'STR_STREET_ADDRESS',
      'STR_CITY',
      'STR_STATE',
      'STR_POSTAL_CODE',
    ];

    const isRequiredField = requiredFields.some((field) =>
      fieldId.includes(field),
    );

    // Only show error if the field is required AND empty
    if (isRequiredField && currentValue.trim() === '') {
      return 'This field is required';
    }

    return '';
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    setEditValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));

    // Check if this change affects mailing or home address
    const isMailingField = fieldId.includes('_mailing');
    const isHomeField = fieldId.includes('_home');

    if (isMailingField) {
      // Check if mailing address has any changes
      const mailingFields = [
        'STR_STREET_ADDRESS_mailing',
        'STR_APT_SUITE_UNIT_FLOOR_mailing',
        'STR_CITY_mailing',
        'STR_STATE_mailing',
        'STR_POSTAL_CODE_mailing',
      ];

      const hasMailingChangesNow = mailingFields.some((field) => {
        const currentValue =
          (field === fieldId ? value : editValues[field]) || '';
        let initialValue = '';

        if (field.includes('STR_STREET_ADDRESS_mailing')) {
          initialValue = initialMailingAddress.address1 || '';
        } else if (field.includes('STR_APT_SUITE_UNIT_FLOOR_mailing')) {
          initialValue = initialMailingAddress.address2 || '';
        } else if (field.includes('STR_CITY_mailing')) {
          initialValue = initialMailingAddress.city || '';
        } else if (field.includes('STR_STATE_mailing')) {
          initialValue = initialMailingAddress.state || '';
        } else if (field.includes('STR_POSTAL_CODE_mailing')) {
          initialValue = initialMailingAddress.postal || '';
        }

        return currentValue !== initialValue;
      });

      setHasMailingChanges(hasMailingChangesNow);
    }

    if (isHomeField) {
      // Check if home address has any changes
      const homeFields = [
        'STR_STREET_ADDRESS_home',
        'STR_APT_SUITE_UNIT_FLOOR_home',
        'STR_CITY_home',
        'STR_STATE_home',
        'STR_POSTAL_CODE_home',
      ];

      const hasHomeChangesNow = homeFields.some((field) => {
        const currentValue =
          (field === fieldId ? value : editValues[field]) || '';
        let initialValue = '';

        if (field.includes('STR_STREET_ADDRESS_home')) {
          initialValue = initialResidentialAddress.address1 || '';
        } else if (field.includes('STR_APT_SUITE_UNIT_FLOOR_home')) {
          initialValue = initialResidentialAddress.address2 || '';
        } else if (field.includes('STR_CITY_home')) {
          initialValue = initialResidentialAddress.city || '';
        } else if (field.includes('STR_STATE_home')) {
          initialValue = initialResidentialAddress.state || '';
        } else if (field.includes('STR_POSTAL_CODE_home')) {
          initialValue = initialResidentialAddress.postal || '';
        }

        return currentValue !== initialValue;
      });

      setHasHomeChanges(hasHomeChangesNow);
    }
  };

  return {
    addresses,
    contents,
    transformAddress,
    handleContent,
    editValues,
    canSave,
    getFieldError,
    handleFieldChange,
    hasAnyAddressChanges,
    hasMailingChanges,
    hasHomeChanges,
  };
};
